import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Flame, DollarSign } from 'lucide-react';
import { toast } from 'react-toastify';
import { accountsService } from '../../../services/accountsService';
import Breadcrumb from '../../../components/Common/Breadcrumb';

const formatINR = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(v || 0);

const getStatus = (runwayMonths) => {
  if (runwayMonths == null || runwayMonths < 0) return 'UNKNOWN';
  if (runwayMonths >= 6) return 'SAFE';
  if (runwayMonths >= 3) return 'WARNING';
  return 'CRITICAL';
};

const StatusBadge = ({ status }) => {
  const cls =
    status === 'SAFE'
      ? 'bg-success'
      : status === 'WARNING'
        ? 'bg-warning text-dark'
        : status === 'CRITICAL'
          ? 'bg-danger'
          : 'bg-secondary';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const RunwaySensitivityReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const r = await accountsService.getRunwaySensitivityReport();
      if (r?.success) setData(r.data);
      else setData(null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load runway sensitivity report');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Accounts', path: '/accounts', onClick: () => navigate('/accounts') },
    { label: 'Reports', path: '/accounts?tab=reports', onClick: () => navigate('/accounts?tab=reports') },
    { label: 'Runway Sensitivity', path: '/accounts/reports/runway-sensitivity', isCurrent: true }
  ];

  const scenarios = data?.scenarios || [];

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div>
          <h1 style={{ fontSize: '22px', margin: '0 0 4px 0' }}>Runway Sensitivity Report</h1>
          <p style={{ fontSize: '12px', margin: 0, color: 'var(--color-text-secondary, #5B6478)' }}>
            Scenario analysis: burn & pipeline impact on runway
          </p>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</p>
        </div>
      )}

      {!loading && data && (
        <>
          <div className="metric-card revenue" style={{ marginBottom: '24px', maxWidth: 320 }}>
            <div className="metric-header">
              <div className="metric-icon"><DollarSign size={16} /></div>
            </div>
            <div className="metric-value">{formatINR(data.cashTotal)}</div>
            <div className="metric-title">Cash Balance</div>
            <div className="metric-subtitle">As of today · Burn: {formatINR(data.currentBurn)}/30d · Forecast: {formatINR(data.forecast)}</div>
          </div>

          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Scenarios</h3>
          <div
            className="dashboard-metrics-grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}
          >
            {scenarios.map((s, i) => (
              <div
                key={i}
                className="metric-card"
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1.25rem',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="metric-icon" style={{ margin: 0 }}>
                      <Flame size={16} />
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '15px' }}>{s.name}</span>
                  </div>
                  <StatusBadge status={getStatus(s.runwayMonths)} />
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px' }}>
                  {s.runwayMonths != null ? `${s.runwayMonths} months` : '—'}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>Runway at scenario</div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && !data && (
        <div className="alert alert-info">Unable to load runway sensitivity report.</div>
      )}
    </div>
  );
};

export default RunwaySensitivityReport;
